import {
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Fade,
  IconButton,
  Tooltip,
  Switch,
  TableSortLabel,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import api from "../axiosInstance";
import { Link } from "react-router-dom";
import NewsModal from "./NewsModal";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { toast, ToastContainer } from "react-toastify";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import CouponModal from "./CouponModal";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import { Checkbox, FormControlLabel, Menu } from "@mui/material";
import { useAuth } from "./access/AuthContext";

function Coupons() {
  const { user } = useAuth;
  const [openNew, setOpenNew] = useState(false);
  const handleNewClose = () => setOpenNew(false);
  const [couponList, setCouponList] = useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(30);
  const [totalElements, setTotalElements] = useState(0);
  const [newsEdit, setNewsEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [extendRow, setExtendRow] = useState(null);
  const [extendDate, setExtendDate] = useState("");
  const [isExtending, setIsExtending] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState(() => {
    return localStorage.getItem("coupons_management") || "";
  });
  const [dense, setDense] = useState(true);
  const [orderBy, setOrderBy] = useState("title");
  const [order, setOrder] = useState("asc");
  // Save to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("coupons_management", searchQuery);
  }, [searchQuery]);
  // Sorting logic
  const handleSort = (columnKey) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };

  const sortedData = useMemo(() => {
    if (!Array.isArray(couponList)) return [];
    if (!orderBy) return couponList;
    return [...couponList].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [couponList, order, orderBy]);

  // padding Dense
  const handleChangeDense = (event) => {
    setDense(event.target.checked);
  };

  // Deletion confirm dialog box open and close.
  const handleClickOpen = (id) => {
    setSelectedId(id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedId(null);
  };
  // Column config and visibility state
  const allColumns = [
    { key: "id", label: "ID", getValue: (r) => r.id },
    {
      key: "targetUserId",
      label: "Target User ID",
      cellCss: { minWidth: "120px" },
      getValue: (r) => r.targetUserId ?? "-",
    },

    {
      key: "code",
      label: "Coupon Code",
      cellCss: { minWidth: "120px" },
      getValue: (r) => r.code,
    },
    {
      key: "categoryid",
      label: "Category Id",
      cellCss: { minWidth: "120px" },
      getValue: (r) => r.category?.id ?? "-",
    },
    {
      key: "targetUniversityId",
      label: "University Id",
      cellCss: { minWidth: "120px" },
      getValue: (r) => r.targetUniversityId ?? "-",
    },
    {
      key: "targetCollegeId",
      label: "College Id",
      cellCss: { minWidth: "120px" },
      getValue: (r) => r.targetCollegeId ?? "-",
    },
    {
      key: "targetCourseId",
      label: "Course Id",
      cellCss: { minWidth: "120px" },
      getValue: (r) => r.targetCourseId ?? "-",
    },
    {
      key: "targetUniversityName",
      label: "University",
      cellCss: { minWidth: "120px" },
      getValue: (r) => r.targetUniversityName ?? "-",
    },
    {
      key: "targetCollegeName",
      label: "College",
      cellCss: { minWidth: "120px" },
      getValue: (r) => r.targetCollegeName ?? "-",
    },
    {
      key: "targetCourseName",
      label: "Course",
      cellCss: { minWidth: "120px" },
      getValue: (r) => r.targetCourseName ?? "-",
    },
    {
      key: "couponReference",
      label: "Coupon Reference",
      cellCss: { minWidth: "150px" },
      getValue: (r) => r.couponReference,
    },
    {
      key: "description",
      label: "Description",
      cellCss: { minWidth: "150px" },
      getValue: (r) => r.description,
    },
    {
      key: "discountType",
      label: "Discount Type",
      cellCss: { minWidth: "120px" },
      getValue: (r) => r.discountType || "-",
    },
    {
      key: "usageStatus",
      label: "Usage Status",
      cellCss: { minWidth: "140px" },
      getValue: (r) =>
        `${r.usageStatus || "-"} [ ${r.couponUsageDate || "-"} ]`,
    },
    {
      key: "discountValue",
      label: "Discount Value",
      cellCss: { minWidth: "130px" },
      getValue: (r) => r.discountValue ?? "-",
    },
    {
      key: "isCollegeSpecific",
      label: "College Specific",
      cellCss: { minWidth: "140px" },
      getValue: (r) => (r.isCollegeSpecific ? "Yes" : "No"),
    },
    {
      key: "isUniversitySpecific",
      label: "University Specific",
      cellCss: { minWidth: "140px" },
      getValue: (r) => (r.isUniversitySpecific ? "Yes" : "No"),
    },
    {
      key: "isCourseSpecific",
      label: "Course Specific",
      cellCss: { minWidth: "140px" },
      getValue: (r) => (r.isCourseSpecific ? "Yes" : "No"),
    },
    {
      key: "isUserSpecific",
      label: "User Specific",
      cellCss: { minWidth: "120px" },
      getValue: (r) => (r.isUserSpecific ? "Yes" : "No"),
    },
    {
      key: "maxLimitDiscount",
      label: "Max Limit Discount",
      cellCss: { minWidth: "170px" },
      getValue: (r) => r.maxLimitDiscount,
    },
    {
      key: "minSpend",
      label: "Min Spend",
      cellCss: { minWidth: "100px" },
      getValue: (r) => r.minSpend,
    },
    {
      key: "sharable",
      label: "Sharable",
      getValue: (r) => (r.sharable ? "Yes" : "No"),
    },
    {
      key: "supportsPartialUsage",
      label: "Supports Partial Usage",
      cellCss: { minWidth: "200px" },
      getValue: (r) => (r.supportsPartialUsage ? "Yes" : "No"),
    },
    {
      key: "usageLimit",
      label: "Usage Limit",
      cellCss: { minWidth: "120px" },
      getValue: (r) => r.usageLimit,
    },
    {
      key: "usageCount",
      label: "Usage Count",
      cellCss: { minWidth: "120px" },
      getValue: (r) => r.usageCount,
    },
    {
      key: "perUserUsageLimit",
      label: "Per User Usage Limit",
      cellCss: { minWidth: "170px" },
      getValue: (r) => r.perUserUsageLimit,
    },
    {
      key: "viewable",
      label: "Viewable",
      getValue: (r) => r.viewable,
    },
    {
      key: "targetUserName",
      label: "Target User Name",
      cellCss: { minWidth: "150px" },
      getValue: (r) => r.targetUser?.firstName || "-",
    },
    {
      key: "targetUserPhone",
      label: "Target User Phone",
      cellCss: { minWidth: "150px" },
      getValue: (r) => r.targetUser?.phoneNumber || "-",
    },
    {
      key: "remainingBalance",
      label: "Remaining Balance",
      cellCss: { minWidth: "150px" },
      getValue: (r) => r.remainingBalance ?? 0,
    },
    {
      key: "active",
      label: "Active",
      getValue: (r) => r.active,
    },
    {
      key: "startDate",
      label: "Start Date",
      cellCss: { minWidth: "100px" },
      getValue: (r) =>
        r.startDate ? new Date(r.startDate).toLocaleString() : "-",
    },
    {
      key: "endDate",
      label: "End Date",
      cellCss: { minWidth: "100px" },
      getValue: (r) => (r.endDate ? new Date(r.endDate).toLocaleString() : "-"),
    },
  ];
  const defaultVisibleKeys = new Set([
    "code",
    "couponReference",
    "description",
    "discountType",
    "category_code",
    "discountValue",
    "isCollegeSpecific",
    "isUserSpecific",
    "isUniversitySpecific",
    "isCourseSpecific",
    "targetUniversityName",
    "targetCollegeName",
    "targetCourseName",
    "maxLimitDiscount",
    "minSpend",
    "sharable",
    "supportsPartialUsage",
    "usageLimit",
    "viewable",
  ]);

  const STORAGE_KEY = "couponsVisibleColumns";

  // Initialize from localStorage synchronously to avoid a race with the persister effect
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          const validKeys = new Set(allColumns.map((c) => c.key));
          const filtered = arr.filter((k) => validKeys.has(k));
          if (filtered.length > 0) {
            return new Set(filtered);
          }
        }
      }
    } catch {}
    return new Set(Array.from(defaultVisibleKeys));
  });

  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const openColumnsMenu = Boolean(columnsMenuAnchor);

  // Persist column config to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(Array.from(visibleColumns)),
      );
    } catch {}
  }, [visibleColumns]);

  const handleOpenColumnsMenu = (e) => setColumnsMenuAnchor(e.currentTarget);
  const handleCloseColumnsMenu = () => setColumnsMenuAnchor(null);
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Helper function to get boolean chip styling
  const getBooleanChip = (value) => {
    console.log(value);

    return (
      <Chip
        label={value ? "Yes" : "No"}
        color={value ? "success" : "error"}
        size="small"
        variant="outlined"
      />
    );
  };

  // Helper function to get discount type chip styling
  const getDiscountTypeChip = (type) => {
    const typeConfig = {
      percentage: { color: "primary", label: "Percentage" },
      fixed: { color: "secondary", label: "Fixed" },
      free: { color: "success", label: "Free" },
    };

    const config = typeConfig[type?.toLowerCase()] || {
      color: "default",
      label: type || "Unknown",
    };
    return <Chip label={config.label} color={config.color} size="small" />;
  };
  // Add this new state to store the debounced query
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);

  // Debounce Effect — waits 500ms after user stops typing
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  // Fetch paged coupons whenever page/rowsPerPage changes
  useEffect(() => {
    setIsLoading(true);
    setError(null);
    api
      .get("/api/v1/coupons/paged", {
        params: { page, size: rowsPerPage, search: debouncedSearch },
      })
      .then((res) => {
        const data = res.data;
        setCouponList(Array.isArray(data?.content) ? data.content : []);
        setTotalElements(
          typeof data?.totalElements === "number" ? data.totalElements : 0,
        );
        setError(null);
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to load coupons. Please try again.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [debouncedSearch, page, rowsPerPage]);

  //   Page nation handel changes
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // page nation rows handles
  const handleChangeRowsPerPage = (event) => {
    const next = parseInt(event.target.value);
    setRowsPerPage(next);
    setPage(0);
  };
  // Delete The record
  const handleDelete = async () => {
    try {
      await api.delete(`/api/v1/coupons/${selectedId}`);

      // Refetch current page after delete
      const currentPage = page;
      const currentSize = rowsPerPage;
      const res = await api.get("/api/v1/coupons/paged", {
        params: { page: currentPage, size: currentSize },
      });
      const data = res.data;

      setCouponList(Array.isArray(data?.content) ? data.content : []);
      setTotalElements(
        typeof data?.totalElements === "number" ? data.totalElements : 0,
      );

      toast.success("Record deleted successfully.!", {
        position: "top-right",
        autoClose: 3000,
      });
      setOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete the record. Please try again.");
    }
  };
  // Adding page data in onclick
  const handleNewOpen = () => {
    setOpenNew(true);
    setNewsEdit([]);
  };
  // Editing page data in onclick
  const handleEditClick = (rowData) => {
    handleNewOpen();
    setNewsEdit(rowData);
  };

  const handleExtendOpen = (row) => {
    setExtendRow(row);
    setExtendDate("");
  };

  // Empty date = backend extends by 3 months
  const handleExtend = async () => {
    setIsExtending(true);
    try {
      const res = await api.put(
        `/api/v1/coupons/${extendRow.id}/extend`,
        null,
        {
          params: extendDate ? { endDate: extendDate } : {},
        },
      );
      handleFormSuccess(res.data?.data?.coupon);
      toast.success("Coupon end date extended.");
      setExtendRow(null);
    } catch (error) {
      console.error("Extend failed:", error);
      toast.error(
        error.response?.data?.errorMessage || "Failed to extend the coupon.",
      );
    } finally {
      setIsExtending(false);
    }
  };

  const visibleColumnDefs = allColumns.filter((c) => visibleColumns.has(c.key));

  // After successful add/update
  const handleFormSuccess = (newItem) => {
    if (!newItem || !newItem.id) {
      console.warn("Invalid item received from form", newItem);
      return;
    }
    // update locally
    setCouponList((prev) => {
      const exists = prev.some((item) => item.id === newItem.id);
      // If editing: replace the item
      if (exists) {
        return prev.map((item) => (item.id === newItem.id ? newItem : item));
      } else {
        // If adding: push new item
        return [newItem, ...prev];
      }
    });

    setNewsEdit(null);
  };
  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <ToastContainer />

      {/* Header Section */}
      {/* <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box>
              <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                <Link underline="hover" color="inherit" to="/booklist">
                  Books List
                </Link>
                <Typography sx={{ color: "text.primary", fontWeight: 600 }}>
                  Coupons Management
                </Typography>
              </Breadcrumbs>
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontWeight: 600, color: "primary.main" }}
              >
                Coupons
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
              disabled={isLoading}
              sx={{ minWidth: 120 }}
            >
              {isLoading ? <CircularProgress size={20} /> : "Refresh"}
            </Button>
          </Box>

         
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleNewOpen}
              sx={{ minWidth: 140 }}
            >
              Add Coupon
            </Button>
            <Button
              variant="outlined"
              startIcon={<ViewColumnIcon />}
              onClick={handleOpenColumnsMenu}
              sx={{ minWidth: 120 }}
            >
              Columns
            </Button>
            <Menu
              anchorEl={columnsMenuAnchor}
              open={openColumnsMenu}
              onClose={handleCloseColumnsMenu}
            >
              {allColumns.map((col) => (
                <FormControlLabel
                  key={col.key}
                  control={
                    <Checkbox
                      checked={visibleColumns.has(col.key)}
                      onChange={() => toggleColumn(col.key)}
                    />
                  }
                  label={col.label}
                  sx={{ pl: 1, pr: 2 }}
                />
              ))}
            </Menu>
          </Stack>
        </CardContent>
      </Card> */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" }, // column on mobile, row on desktop
          justifyContent: { xs: "flex-start", md: "space-between" }, // start on mobile, space-between on desktop
          alignItems: { xs: "stretch", md: "center" }, // stretch on mobile, center on desktop
          gap: { xs: 1, md: 0 }, // spacing between items
          mb: 2,
          marginLeft: "10px",
        }}
      >
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
          <Link underline="hover" color="inherit" to="/booklist">
            Books List
          </Link>
          <Typography sx={{ color: "text.primary", fontWeight: 600 }}>
            Coupons Management
          </Typography>
        </Breadcrumbs>

        <Box>
          {/* Action Buttons */}
          <Stack
            direction="row"
            spacing={2}
            flexWrap="wrap"
            useFlexGap
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: { xs: "wrap", md: "nowrap" }, // wrap buttons on mobile
              mt: { xs: 1, md: 0 },
              marginRight: "10px",
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ViewColumnIcon />}
              onClick={handleOpenColumnsMenu}
              sx={{
                minWidth: { xs: "120px", md: "auto" }, // smaller on mobile
                fontSize: { xs: "10px", md: "12px" },
              }}
            >
              Columns
            </Button>
            <Menu
              anchorEl={columnsMenuAnchor}
              open={openColumnsMenu}
              onClose={handleCloseColumnsMenu}
              PaperProps={{
                sx: {
                  width: 270,
                  maxHeight: 500,
                  overflowY: "auto",
                },
              }}
            >
              {allColumns.map((col) => (
                <FormControlLabel
                  key={col.key}
                  control={
                    <Checkbox
                      checked={visibleColumns.has(col.key)}
                      onChange={() => toggleColumn(col.key)}
                    />
                  }
                  label={col.label}
                  sx={{ pl: 1, pr: 2 }}
                />
              ))}
            </Menu>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleNewOpen}
              sx={{
                minWidth: { xs: "120px", md: "auto" }, // smaller on mobile
                fontSize: { xs: "10px", md: "12px" },
              }}
            >
              Add Coupon
            </Button>
          </Stack>
        </Box>
      </Box>
      {/* Search Section */}
      <Card sx={{ boxShadow: 1 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                minWidth: { xs: "80%", md: "50%" },
                display: "flex",
                width: "50%",
                alignItems: "center",
                gap: 2,
              }}
            >
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Search Id,Code,Description,Discount type & value..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ maxWidth: 400 }}
              />
              <Typography
                sx={{ display: { xs: "none", md: "block" } }}
                variant="body2"
                color="text.secondary"
              >
                {
                  sortedData.filter(
                    (coupon) =>
                      coupon.id
                        ?.toString()
                        .toLowerCase()
                        .includes(searchQuery.toLowerCase()) ||
                      coupon.code
                        ?.toLowerCase()
                        .includes(searchQuery.toLowerCase()) ||
                      coupon.discount_type
                        ?.toLowerCase()
                        .includes(searchQuery.toLowerCase()) ||
                      coupon.discount_value
                        ?.toLowerCase()
                        .includes(searchQuery.toLowerCase()) ||
                      coupon.creation_date
                        ?.toLowerCase()
                        .includes(searchQuery.toLowerCase()) ||
                      coupon.description
                        ?.toLowerCase()
                        .includes(searchQuery.toLowerCase()),
                  ).length
                }{" "}
                of {totalElements} coupons
              </Typography>
            </Box>
            <Box>
              {/* Dense toggle */}
              <Tooltip title="Dense padding" arrow>
                <FormControlLabel
                  control={
                    <Switch
                      checked={dense}
                      onChange={handleChangeDense}
                      color="primary"
                    />
                  }
                />
              </Tooltip>
            </Box>
          </Box>
          <Typography
            sx={{ display: { xs: "block", md: "none" }, textAlign: "left" }}
            variant="body2"
            color="text.secondary"
          >
            {
              sortedData.filter(
                (coupon) =>
                  coupon.id
                    ?.toString()
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase()) ||
                  coupon.code
                    ?.toLowerCase()
                    .includes(searchQuery.toLowerCase()) ||
                  coupon.discount_type
                    ?.toLowerCase()
                    .includes(searchQuery.toLowerCase()) ||
                  coupon.discount_value
                    ?.toLowerCase()
                    .includes(searchQuery.toLowerCase()) ||
                  coupon.creation_date
                    ?.toLowerCase()
                    .includes(searchQuery.toLowerCase()) ||
                  coupon.description
                    ?.toLowerCase()
                    .includes(searchQuery.toLowerCase()),
              ).length
            }{" "}
            of {totalElements} coupons
          </Typography>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {/* Table Section */}
      {!isLoading && (
        <Fade in={!isLoading}>
          <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: 2 }}>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table
                stickyHeader
                aria-label="coupons table"
                size={dense ? "small" : "medium"}
              >
                <TableHead>
                  <TableRow>
                    {visibleColumnDefs.map((c) => (
                      <TableCell
                        key={c.key}
                        sx={{
                          backgroundColor: "#17479e",
                          padding: "10px 5px ",
                          color: "white",
                          fontWeight: 600,
                          ...c.cellCss,
                        }}
                        sortDirection={orderBy === c.key ? order : false}
                      >
                        <TableSortLabel
                          active={orderBy === c.key}
                          direction={orderBy === c.key ? order : "asc"}
                          onClick={() => handleSort(c.key)}
                          sx={{
                            color: "#ffffff !important",
                            "&.Mui-active": {
                              color: "#ffffff !important",
                            },
                            "& .MuiTableSortLabel-icon": {
                              color: "#ffffff !important",
                            },
                          }}
                        >
                          {c.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell
                      sx={{
                        backgroundColor: "#17479e",
                        color: "white",
                        align: "center",
                        fontWeight: 600,
                        minWidth: 120,
                        position: "sticky",
                        right: 0,
                        zIndex: 2,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(sortedData) && sortedData.length > 0 ? (
                    sortedData
                      .filter(
                        (coupon) =>
                          coupon.id
                            ?.toString()
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase()) ||
                          coupon.code
                            ?.toLowerCase()
                            .includes(searchQuery.toLowerCase()) ||
                          coupon.discount_type
                            ?.toLowerCase()
                            .includes(searchQuery.toLowerCase()) ||
                          coupon.discount_value
                            ?.toLowerCase()
                            .includes(searchQuery.toLowerCase()) ||
                          coupon.creation_date
                            ?.toLowerCase()
                            .includes(searchQuery.toLowerCase()) ||
                          coupon.description
                            ?.toLowerCase()
                            .includes(searchQuery.toLowerCase()),
                      )
                      .map((row) => (
                        <TableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={row.id}
                          sx={{
                            "&:nth-of-type(odd)": {
                              backgroundColor: "rgba(0, 0, 0, 0.02)",
                            },
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          {visibleColumnDefs.map((c) => (
                            <TableCell
                              key={c.key}
                              sx={{
                                padding: "5px 5px",
                                align: "center",
                                ...c.cellCss,
                              }}
                            >
                              {c.key === "isCollegeSpecific" ||
                              c.key === "isUserSpecific" ||
                              c.key === "sharable" ||
                              c.key === "supportsPartialUsage" ||
                              c.key === "viewable" ||
                              c.key === "active"
                                ? getBooleanChip(c.getValue(row))
                                : c.key === "discountType"
                                  ? getDiscountTypeChip(c.getValue(row))
                                  : c.getValue(row)}
                            </TableCell>
                          ))}
                          <TableCell
                            sx={{
                              align: "center",
                              padding: "10px ",
                              minWidth: "120px",
                              position: "sticky",
                              right: 0,
                              background: "#fff",
                              zIndex: 1,
                            }}
                          >
                            {/* {row.usageStatus === "Used" ? (
                              ""
                            ) : ( */}
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="center"
                            >
                              <Tooltip
                                title="Edit Coupon"
                                arrow
                                placement="top"
                              >
                                <IconButton
                                  color="primary"
                                  onClick={() => handleEditClick(row)}
                                  size="small"
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: "primary.light",
                                      color: "white",
                                    },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip
                                title="Extend Coupon"
                                arrow
                                placement="top"
                              >
                                <IconButton
                                  color="success"
                                  onClick={() => handleExtendOpen(row)}
                                  size="small"
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: "success.light",
                                      color: "white",
                                    },
                                  }}
                                >
                                  <MoreTimeIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip
                                title="Delete Coupon"
                                arrow
                                placement="top"
                              >
                                <IconButton
                                  color="error"
                                  onClick={() => handleClickOpen(row.id)}
                                  size="small"
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: "error.light",
                                      color: "white",
                                    },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                            {/* )} */}
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={visibleColumnDefs.length + 1}
                        sx={{ textAlign: "center", py: 4 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Typography variant="h6" color="text.secondary">
                            {searchQuery
                              ? "No coupons found matching your search"
                              : "No coupons available"}
                          </Typography>
                          {searchQuery && (
                            <Button
                              variant="outlined"
                              onClick={() => setSearchQuery("")}
                              startIcon={<SearchIcon />}
                            >
                              Clear Search
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 30, 50, 100, 200]}
              component="div"
              count={totalElements}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                "& .MuiTablePagination-toolbar": {
                  paddingLeft: 2,
                  paddingRight: 2,
                },
              }}
            />
          </Paper>
        </Fade>
      )}
      {/* Modals */}
      <CouponModal
        newsEdit={newsEdit}
        open={openNew}
        onClose={handleNewClose}
        onSuccess={handleFormSuccess}
      />

      {/* Extend Coupon Dialog */}
      <Dialog
        open={!!extendRow}
        onClose={() => !isExtending && setExtendRow(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Extend Coupon</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {extendRow?.code} ends on{" "}
            {extendRow?.endDate
              ? new Date(extendRow.endDate).toLocaleString()
              : "-"}
            . Pick a new end date, or leave it empty to extend by 3 months.
          </DialogContentText>
          <TextField
            type="date"
            label="New end date"
            value={extendDate}
            onChange={(e) => setExtendDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtendRow(null)} disabled={isExtending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleExtend}
            disabled={isExtending}
          >
            {extendDate ? "Extend" : "Extend 3 months"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <DeleteConfirmationDialog
        open={open}
        onClose={handleClose}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this coupon? This action cannot be undone."
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </Box>
  );
}

export default Coupons;
